import React, { useRef, useEffect, useCallback } from "react";

const noop = () => {};

interface ContentEditableProps {
  id: string;
  className?: string;
  tagName?: keyof React.JSX.IntrinsicElements;
  value: string;
  contentEditable?: boolean;
  onChange?: (value: string) => void;
  onFocus?: () => void;
}

function ContentEditable({
  id,
  className,
  value,
  onChange = noop,
  onFocus,
  tagName = 'div',
  contentEditable = true,
  ...props
}: ContentEditableProps) {
  const elementRef = useRef<HTMLElement>(null);
  const currentValueRef = useRef<string>(value);

  const handleInput = useCallback((event: React.FormEvent<HTMLElement>) => {
    const newValue = event.currentTarget.innerText || '';
    currentValueRef.current = newValue;
    onChange(newValue);
  }, [onChange]);

  // Initialize content on mount
  useEffect(() => {
    const element = elementRef.current;
    if (element && element.innerText === '') {
      element.innerText = value;
      currentValueRef.current = value;
    }
  }, []);

  // Update DOM only when external value differs from current user input
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Only update if the incoming value is different from what the user has typed
    if (value !== currentValueRef.current) {
      element.innerText = value;
      currentValueRef.current = value;
    }
  }, [value]);

  // Create element with custom tag name
  // Don't pass value as children to prevent React from updating it
  return React.createElement(
    tagName,
    {
      ...props,
      id,
      className,
      ref: elementRef,
      contentEditable: contentEditable,
      suppressContentEditableWarning: true,
      onInput: handleInput,
      onFocus: onFocus,
      role: 'textbox',
    }
  );
}

export default ContentEditable;